export type CustomMeasurementUnit = "monthOrYear" | "mile"

/**
 * Converts numerical values into a readable string format, automatically choosing between months and years.
 * @param value The numerical value to convert. This is expected to be the number of days when 'monthOrYear' is used.
 * @param unit The type of custom measurement unit (monthOrYear, mile), with "monthOrYear" as the default.
 * @returns A string representing the value with the appropriate unit in singular or plural form.
 */
export function formatCustomUnit(
  value: number,
  unit: CustomMeasurementUnit = "monthOrYear"
): string {
  if (unit === "mile") {
    return `${value} mile${value === 1 ? "" : "s"}`
  }

  // Calculate years or months based on the number of days
  const years = value / 365
  if (years >= 1) {
    let yearText = years.toFixed(1)
    yearText = yearText === "1.0" ? "1" : yearText // Replace '1.0' with '1'
    return `${yearText} year${parseFloat(yearText) === 1 ? "" : "s"}`
  }

  const months = Math.round(value / 30)
  return `${months} month${months === 1 ? "" : "s"}`
}

/* Usage Examples:
  console.log(formatCustomUnit(365))               // "1 year"
  console.log(formatCustomUnit(547))               // "1.5 years"
  console.log(formatCustomUnit(90))                // "3 months"
  console.log(formatCustomUnit(1000, 'mile'))      // "1000 miles"
*/
